import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Search, Building2, Users, FolderKanban, FileText,
  Receipt, Presentation, ChevronRight, Loader2, X,
} from 'lucide-react';

import { API } from '../../lib/api';

// Entity type icon mapping
const ENTITY_ICONS = {
  clients: Building2,
  contacts: Users,
  projects: FolderKanban,
  quotes: FileText,
  invoices: Receipt,
  proposals: Presentation,
};

// Entity type pill colors
const ENTITY_COLORS = {
  clients: { bg: '#e0e7ff', text: '#3730a3' },
  contacts: { bg: '#fce7f3', text: '#9d174d' },
  projects: { bg: '#fef3c7', text: '#92400e' },
  quotes: { bg: '#dbeafe', text: '#1d4ed8' },
  invoices: { bg: '#dcfce7', text: '#166534' },
  proposals: { bg: '#ede9fe', text: '#5b21b6' },
};

// Status badge for invoices/proposals
function StatusBadge({ status }) {
  if (!status) return null;
  const colors = {
    draft: { bg: '#f3f4f6', text: '#6b7280' },
    sent: { bg: '#dbeafe', text: '#1d4ed8' },
    paid: { bg: '#dcfce7', text: '#16a34a' },
    accepted: { bg: '#dcfce7', text: '#16a34a' },
    declined: { bg: '#fee2e2', text: '#dc2626' },
    planning: { bg: '#dbeafe', text: '#1d4ed8' },
    active: { bg: '#dcfce7', text: '#16a34a' },
    completed: { bg: '#f3f4f6', text: '#6b7280' },
    on_hold: { bg: '#fef3c7', text: '#d97706' },
    lead: { bg: '#e0e7ff', text: '#3730a3' },
  };
  const c = colors[status] || colors.draft;
  return (
    <span
      className="inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded"
      style={{ background: c.bg, color: c.text }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// Format currency
function fmt$(num) {
  if (num == null) return '';
  return '$' + Number(num).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// ─────────────────────────────────────────────────────────────────────────────
export default function SearchTab() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]); // which entity types to show

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults(null);
      return;
    }
    const timer = setTimeout(() => performSearch(), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function performSearch() {
    setLoading(true);
    try {
      const typesParam = activeFilters.length > 0 ? `&types=${activeFilters.join(',')}` : '';
      const res = await axios.get(
        `${API}/search?q=${encodeURIComponent(query.trim())}${typesParam}`
      );
      setResults(res.data);
    } catch (err) {
      console.error('Search error:', err);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  // Toggle entity type filter
  function toggleFilter(type) {
    setActiveFilters(prev => {
      const next = prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type];
      return next;
    });
  }

  // Re-search when filters change
  useEffect(() => {
    if (query && query.trim().length >= 2) {
      performSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters]);

  // Navigate to entity detail
  function handleResultClick(entityType, id) {
    const routeMap = {
      clients: `/clients/${id}`,
      contacts: `/contacts/${id}`,
      projects: `/projects/${id}`,
      quotes: `/quote/${id}`,
      invoices: `/invoice/${id}`,
      proposals: `/proposal/${id}`,
    };
    const path = routeMap[entityType];
    if (path) navigate(path);
  }

  // Get display value from item
  function getTitle(item, entityType) {
    const fieldMap = {
      clients: 'company_name',
      contacts: 'full_name',
      projects: 'project_name',
      quotes: 'quote_no',
      invoices: 'invoice_no',
      proposals: 'proposal_no',
    };
    return item[fieldMap[entityType]] || '—';
  }

  function getSub(item, entityType) {
    const fieldMap = {
      clients: item.trading_name || item.industry || '',
      contacts: item.company || item.title || '',
      projects: item.client_name || item.site_address || '',
      quotes: item.client_name || item.project_name || '',
      invoices: item.client_name || item.project_name || '',
      proposals: item.quote_no || item.variables?.client_name || '',
    };
    return fieldMap[entityType] || '';
  }

  const entityTypes = ['clients', 'contacts', 'projects', 'quotes', 'invoices', 'proposals'];

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#9ca3af' }} />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search across clients, contacts, projects, quotes, invoices, proposals..."
          className="w-full pl-10 pr-10 py-3 rounded-lg border text-sm focus:outline-none focus:ring-2"
          style={{
            borderColor: '#e5e4df',
            background: '#ffffff',
            fontFamily: "'Manrope', sans-serif",
            focusRingColor: '#c9a84c',
          }}
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults(null); }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <X className="w-4 h-4" style={{ color: '#9ca3af' }} />
          </button>
        )}
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-2">
        {entityTypes.map(type => {
          const Icon = ENTITY_ICONS[type];
          const isActive = activeFilters.length === 0 || activeFilters.includes(type);
          const color = ENTITY_COLORS[type];
          return (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-all"
              style={{
                background: isActive ? color.bg : '#f3f2ef',
                color: isActive ? color.text : '#9ca3af',
                border: isActive ? `1px solid ${color.text}30` : '1px solid #e5e4df',
              }}
            >
              <Icon className="w-3 h-3" />
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          );
        })}
        {activeFilters.length > 0 && (
          <button
            onClick={() => setActiveFilters([])}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ color: '#6b7280', background: '#f3f2ef' }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Results */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#c9a84c' }} />
          <span className="ml-2 text-sm" style={{ color: '#6b7280' }}>Searching...</span>
        </div>
      )}

      {!loading && results && results.total === 0 && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 mx-auto mb-3" style={{ color: '#c9a84c', opacity: 0.4 }} />
          <p className="text-sm font-medium" style={{ color: '#1a1a2e' }}>No results found</p>
          <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
            Try different keywords or adjust your filters
          </p>
        </div>
      )}

      {!loading && results && results.total > 0 && (
        <div className="space-y-4">
          {/* Result count */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold" style={{ color: '#6b7280' }}>
              {results.total} result{results.total !== 1 ? 's' : ''} found
            </span>
          </div>

          {/* Results grouped by entity type */}
          {Object.entries(results.results || {}).map(([entityType, group]) => {
            const Icon = ENTITY_ICONS[entityType] || Search;
            const color = ENTITY_COLORS[entityType] || { bg: '#f3f4f6', text: '#6b7280' };
            return (
              <div
                key={entityType}
                className="rounded-lg border overflow-hidden"
                style={{ background: '#ffffff', borderColor: '#e5e4df' }}
              >
                {/* Section header */}
                <div
                  className="flex items-center justify-between px-4 py-2.5 border-b"
                  style={{ borderColor: '#e5e4df', background: '#f8f7f4' }}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: '#c9a84c' }} />
                    <span className="text-sm font-semibold" style={{ color: '#1a1a2e' }}>
                      {group.label}
                    </span>
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: color.bg, color: color.text }}
                    >
                      {group.count}
                    </span>
                  </div>
                </div>

                {/* Items */}
                <div className="divide-y" style={{ borderColor: '#f3f2ef' }}>
                  {group.items.map(item => {
                    const title = getTitle(item, entityType);
                    const sub = getSub(item, entityType);
                    return (
                      <div
                        key={item.id}
                        onClick={() => handleResultClick(entityType, item.id)}
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#f8f7f4] transition-colors group"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate" style={{ color: '#1a1a2e' }}>
                            {title}
                          </div>
                          {sub && (
                            <div className="text-xs truncate mt-0.5" style={{ color: '#6b7280' }}>
                              {sub}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                          {/* Show subtotal for quotes/invoices */}
                          {item.subtotal != null && (
                            <span className="text-xs font-mono font-medium" style={{ color: '#1a1a2e' }}>
                              {fmt$(item.subtotal)}
                            </span>
                          )}
                          {/* Show status badge */}
                          <StatusBadge status={item.status} />
                          <ChevronRight
                            className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ color: '#c9a84c' }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state — no query */}
      {!loading && !results && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 mx-auto mb-3" style={{ color: '#c9a84c', opacity: 0.3 }} />
          <p className="text-sm font-medium" style={{ color: '#1a1a2e' }}>Search everything</p>
          <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
            Type at least 2 characters to search across all modules
          </p>
        </div>
      )}
    </div>
  );
}
